import { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import Calendar from 'react-calendar';
import { endOfDay, format, startOfDay } from 'date-fns';

import 'react-calendar/dist/Calendar.css';

import styles from './DateRangeTimePicker.module.css';

interface Props {
  since: number;
  until: number;
  onSinceChange: (value: number) => void;
  onUntilChange: (value: number) => void;
}

export function DateRangeTimePicker({
  since,
  until,
  onSinceChange,
  onUntilChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fromDate = new Date(since * 1000);
  const toDate = new Date(until * 1000);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleDateRangeChange(value: unknown) {
    if (!Array.isArray(value) || value.length !== 2) return;
    const [from, to] = value as [Date, Date];
    onSinceChange(Math.floor(from.getTime() / 1000));
    onUntilChange(Math.floor(to.getTime() / 1000));
  }

  function handleFromTimeChange(timeStr: string) {
    const time = parseTime(timeStr);
    if (!time) return;
    const newDate = new Date(fromDate);
    newDate.setHours(time.hours, time.minutes, 0, 0);
    onSinceChange(Math.floor(newDate.getTime() / 1000));
  }

  function handleToTimeChange(timeStr: string) {
    const time = parseTime(timeStr);
    if (!time) return;
    const newDate = new Date(toDate);
    newDate.setHours(time.hours, time.minutes, 0, 0);
    onUntilChange(Math.floor(newDate.getTime() / 1000));
  }

  const displayRange = `${format(fromDate, 'yyyy-MM-dd HH:mm:ss')} - ${format(toDate, 'yyyy-MM-dd HH:mm:ss')}`;
  const fromTimeStr = format(fromDate, 'HH:mm');
  const toTimeStr = format(toDate, 'HH:mm');

  return (
    <div className="searchBar relative flex items-center gap-1.5" ref={ref}>
      <input
        type="text"
        readOnly
        className="w-80 cursor-pointer border-0 bg-transparent text-sm font-normal outline-none"
        value={displayRange}
        onClick={() => setIsOpen(!isOpen)}
        placeholder="Select date range"
      />
      <CalendarIcon size={14} className="searchIcon shrink-0" />

      {isOpen && (
        <div
          className={`${styles.popup} absolute right-0 top-full z-50 mt-2 rounded-lg border border-gray-2 p-6 shadow-xl th-highcontrast:border-white th-dark:border-gray-8`}
        >
          <div className="flex gap-6">
            <div className={styles.calendar}>
              <Calendar
                selectRange
                // Day-granular bounds: the calendar highlights whole days only
                // when the range covers them fully; times live in the inputs.
                value={[startOfDay(fromDate), endOfDay(toDate)]}
                onChange={handleDateRangeChange}
              />
            </div>

            <div className="border-l border-gray-3 th-highcontrast:border-white th-dark:border-gray-8">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="observability-from-time"
                    className="mb-2 block text-xs font-semibold text-gray-6 th-highcontrast:text-white th-dark:text-gray-5"
                  >
                    From
                  </label>
                  <input
                    id="observability-from-time"
                    type="time"
                    className="rounded border border-gray-3 bg-transparent px-2 py-1 text-sm th-highcontrast:border-white th-highcontrast:text-white th-dark:border-gray-8 th-dark:text-white"
                    value={fromTimeStr}
                    onChange={(e) => handleFromTimeChange(e.target.value)}
                    data-cy="observability-logs-from-time"
                  />
                </div>

                <div>
                  <label
                    htmlFor="observability-to-time"
                    className="mb-2 block text-xs font-semibold text-gray-6 th-highcontrast:text-white th-dark:text-gray-5"
                  >
                    To
                  </label>
                  <input
                    id="observability-to-time"
                    type="time"
                    className="rounded border border-gray-3 bg-transparent px-2 py-1 text-sm th-highcontrast:border-white th-highcontrast:text-white th-dark:border-gray-8 th-dark:text-white"
                    value={toTimeStr}
                    onChange={(e) => handleToTimeChange(e.target.value)}
                    data-cy="observability-logs-to-time"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// A time input emits '' while the value is incomplete (e.g. mid-edit); ignore those.
function parseTime(timeStr: string): { hours: number; minutes: number } | null {
  const match = /^(\d{2}):(\d{2})/.exec(timeStr);
  if (!match) return null;
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}
